import { createAppRequest } from '@/services/request';
import type { ApiRequestOptions } from './types/common';

const request = createAppRequest();

export interface RuleDefinitionRecord {
  uuid: string;
  code: string;
  name: string;
  description?: string;
  category: string;
  defaultAction: string;
  priority: number;
  businessScope: string;
  effectiveStartAt?: string;
  effectiveEndAt?: string;
  isBreak: boolean;
  isEnabled: boolean;
  version: string;
  publishStatus: string;
  lastPublishedByMemberUuid?: string;
  lastPublishedAt?: string;
  disableReason?: string;
  config?: Record<string, unknown>;
}

export interface RuleDefinitionVersionRecord {
  uuid: string;
  ruleUuid: string;
  code: string;
  name: string;
  description?: string;
  category: string;
  defaultAction: string;
  priority: number;
  businessScope: string;
  effectiveStartAt?: string;
  effectiveEndAt?: string;
  isBreak: boolean;
  version: string;
  config?: Record<string, unknown>;
  publishedByMemberUuid?: string;
  publishedAt: string;
}

export interface RuleExecuteLogRecord {
  uuid: string;
  executeNo: string;
  businessType: string;
  businessNo: string;
  isSimulation: boolean;
  inputSnapshot?: Record<string, unknown>;
  matchedRules: Array<{
    code: string;
    name: string;
    decision: string;
    explanation: string;
  }>;
  finalDecision: string;
  explanation: string;
  elapsedMs: number;
  ruleCount: number;
  matchedRuleCount: number;
  breakRuleCode?: string;
  traceId?: string;
  createdAt: string;
}

export interface SaveRuleDefinitionPayload {
  code?: string;
  name: string;
  description?: string;
  category: string;
  defaultAction: string;
  priority?: number;
  businessScope?: string;
  effectiveStartAt?: string;
  effectiveEndAt?: string;
  isBreak?: boolean;
  isEnabled?: boolean;
  version?: string;
  disableReason?: string;
  config?: Record<string, unknown>;
}

export interface SimulateRulesPayload {
  businessType: string;
  businessNo?: string;
  items: Array<{
    materialUuid: string;
    materialName?: string;
    quantity: number;
    currentQty?: number;
    afterQty?: number;
    amount?: number;
    riskScore?: number;
    applicantMemberUuid?: string;
    departmentUuid?: string;
    requestedAt?: string;
    lastApplyAt?: string;
    isSpecial?: boolean;
    isFixedAsset?: boolean;
  }>;
}

export interface RuleEvaluationResult {
  finalDecision: string;
  explanation: string;
  ruleCount: number;
  matchedRuleCount: number;
  breakRuleCode?: string;
  matchedRules: Array<{
    code: string;
    name: string;
    decision: string;
    explanation: string;
  }>;
}

export async function getRuleDefinitions(options?: ApiRequestOptions) {
  return request<RuleDefinitionRecord[]>('/bff/rule/definitions', {
    method: 'GET',
    ...(options || {}),
  });
}

export async function createRuleDefinition(
  data: SaveRuleDefinitionPayload,
  options?: ApiRequestOptions,
) {
  return request<RuleDefinitionRecord>('/bff/rule/definitions', {
    method: 'POST',
    data,
    ...(options || {}),
  });
}

export async function updateRuleDefinition(
  ruleUuid: string,
  data: SaveRuleDefinitionPayload,
  options?: ApiRequestOptions,
) {
  return request<RuleDefinitionRecord>(`/bff/rule/definitions/${ruleUuid}`, {
    method: 'PUT',
    data,
    ...(options || {}),
  });
}

export async function enableRuleDefinition(ruleUuid: string, options?: ApiRequestOptions) {
  return request<RuleDefinitionRecord>(`/bff/rule/definitions/${ruleUuid}/enable`, {
    method: 'POST',
    data: {},
    ...(options || {}),
  });
}

export async function disableRuleDefinition(ruleUuid: string, options?: ApiRequestOptions) {
  return request<RuleDefinitionRecord>(`/bff/rule/definitions/${ruleUuid}/disable`, {
    method: 'POST',
    data: {},
    ...(options || {}),
  });
}

export async function deleteRuleDefinition(ruleUuid: string, options?: ApiRequestOptions) {
  return request<RuleDefinitionRecord>(`/bff/rule/definitions/${ruleUuid}`, {
    method: 'DELETE',
    ...(options || {}),
  });
}

export async function cloneRuleDefinition(ruleUuid: string, options?: ApiRequestOptions) {
  return request<RuleDefinitionRecord>(`/bff/rule/definitions/${ruleUuid}/clone`, {
    method: 'POST',
    data: {},
    ...(options || {}),
  });
}

export async function publishRuleDefinition(ruleUuid: string, options?: ApiRequestOptions) {
  return request<RuleDefinitionVersionRecord>(
    `/bff/rule/definitions/${ruleUuid}/publish`,
    {
      method: 'POST',
      data: {},
      ...(options || {}),
    },
  );
}

export async function getRuleDefinitionVersions(
  ruleUuid: string,
  options?: ApiRequestOptions,
) {
  return request<RuleDefinitionVersionRecord[]>(
    `/bff/rule/definitions/${ruleUuid}/versions`,
    {
      method: 'GET',
      ...(options || {}),
    },
  );
}

export async function rollbackRuleDefinition(
  ruleUuid: string,
  versionUuid: string,
  options?: ApiRequestOptions,
) {
  return request<RuleDefinitionRecord>(
    `/bff/rule/definitions/${ruleUuid}/versions/${versionUuid}/rollback`,
    {
      method: 'POST',
      data: {},
      ...(options || {}),
    },
  );
}

export async function simulateRules(
  data: SimulateRulesPayload,
  options?: ApiRequestOptions,
) {
  return request<RuleEvaluationResult>('/bff/rule/simulate', {
    method: 'POST',
    data,
    ...(options || {}),
  });
}

export async function getRuleExecuteLogs(options?: ApiRequestOptions) {
  return request<RuleExecuteLogRecord[]>('/bff/rule/execute-logs', {
    method: 'GET',
    ...(options || {}),
  });
}
